import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { apiFetch, ApiError } from "../utils/api";
import { useAuth } from "../hooks/useAuth";
import { Payment } from "../types/paymentTypes";

// Paystack redirects here after checkout with ?reference=...&trxref=...
// We ask the API to verify the reference with Paystack, which also updates
// the payment record (the webhook may not reach local/dev environments).

type Status = "checking" | "success" | "failed" | "pending" | "error";

const AUTO_RETRIES = 3;
const RETRY_DELAY_MS = 3000;

const PaymentCallback: React.FC = () => {
  const [searchParams] = useSearchParams();
  const reference = searchParams.get("reference") || searchParams.get("trxref");
  const { isAuthenticated } = useAuth();

  const [status, setStatus] = useState<Status>("checking");
  const [payment, setPayment] = useState<Payment | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const attempts = useRef(0);
  const retryTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const verify = useCallback(async () => {
    if (!reference) return;
    setStatus("checking");
    try {
      const result: Payment = await apiFetch(
        `/api/payments/verify/${encodeURIComponent(reference)}`,
        { method: "POST" },
        true
      );
      setPayment(result);
      if (result.status === "SUCCESS") {
        setStatus("success");
      } else if (result.status === "FAILED") {
        setStatus("failed");
      } else if (attempts.current < AUTO_RETRIES) {
        // Paystack can take a moment to settle; check again shortly
        attempts.current += 1;
        retryTimer.current = setTimeout(verify, RETRY_DELAY_MS);
      } else {
        setStatus("pending");
      }
    } catch (e) {
      setStatus("error");
      setMessage(
        e instanceof ApiError && e.status === 403
          ? "This payment belongs to a different account."
          : e instanceof Error
          ? e.message
          : "We couldn't confirm your payment."
      );
    }
  }, [reference]);

  useEffect(() => {
    if (reference && isAuthenticated) {
      attempts.current = 0;
      verify();
    }
    return () => {
      if (retryTimer.current) clearTimeout(retryTimer.current);
    };
  }, [reference, isAuthenticated, verify]);

  const checkAgain = () => {
    attempts.current = AUTO_RETRIES; // one manual check, no further auto-retries
    verify();
  };

  const dashboardLink = (
    <Link
      to="/dashboard/customer"
      className="inline-block mt-6 bg-green-600 hover:bg-green-700 text-white font-bold py-2.5 px-6 rounded-lg"
    >
      Go to my bookings
    </Link>
  );

  let body: React.ReactNode;
  if (!reference) {
    body = (
      <>
        <h1 className="text-2xl font-bold text-gray-900">No payment to confirm</h1>
        <p className="mt-2 text-gray-600">
          This page is opened by Paystack after you pay. If you just paid, check
          your bookings for the latest status.
        </p>
        {dashboardLink}
      </>
    );
  } else if (!isAuthenticated) {
    body = (
      <>
        <h1 className="text-2xl font-bold text-gray-900">Log in to confirm your payment</h1>
        <p className="mt-2 text-gray-600">
          Your session has ended. Log in, then open your bookings and tap
          "Refresh payment status" to confirm this payment.
        </p>
        <p className="mt-2 text-xs text-gray-400">Reference: {reference}</p>
        <Link
          to="/login"
          className="inline-block mt-6 bg-green-600 hover:bg-green-700 text-white font-bold py-2.5 px-6 rounded-lg"
        >
          Log in
        </Link>
      </>
    );
  } else if (status === "checking") {
    body = (
      <>
        <h1 className="text-2xl font-bold text-gray-900">Confirming your payment...</h1>
        <p className="mt-2 text-gray-600">This only takes a moment.</p>
      </>
    );
  } else if (status === "success") {
    body = (
      <>
        <h1 className="text-2xl font-bold text-green-700">Payment successful</h1>
        <p className="mt-2 text-gray-600">
          Thank you! Your payment
          {payment ? ` of ₦${payment.amount.toLocaleString()}` : ""} has been received.
        </p>
        {dashboardLink}
      </>
    );
  } else if (status === "failed") {
    body = (
      <>
        <h1 className="text-2xl font-bold text-red-700">Payment not completed</h1>
        <p className="mt-2 text-gray-600">
          The payment was cancelled or declined, and you have not been charged.
          You can try again from your bookings.
        </p>
        {dashboardLink}
      </>
    );
  } else if (status === "pending") {
    body = (
      <>
        <h1 className="text-2xl font-bold text-gray-900">Payment still processing</h1>
        <p className="mt-2 text-gray-600">
          Paystack hasn't confirmed this payment yet. This can take a few
          minutes. Check again shortly.
        </p>
        <button
          type="button"
          onClick={checkAgain}
          className="mt-6 mr-3 py-2.5 px-6 text-sm font-medium text-green-700 border border-green-600 rounded-lg hover:bg-green-50"
        >
          Check again
        </button>
        {dashboardLink}
      </>
    );
  } else {
    body = (
      <>
        <h1 className="text-2xl font-bold text-red-700">We couldn't confirm your payment</h1>
        <p className="mt-2 text-gray-600">{message}</p>
        <button
          type="button"
          onClick={checkAgain}
          className="mt-6 mr-3 py-2.5 px-6 text-sm font-medium text-green-700 border border-green-600 rounded-lg hover:bg-green-50"
        >
          Try again
        </button>
        {dashboardLink}
      </>
    );
  }

  return (
    <section className="bg-gray-50 min-h-[60vh] flex items-center">
      <div className="max-w-lg mx-auto px-6 py-16 text-center">{body}</div>
    </section>
  );
};

export default PaymentCallback;
